
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Expense } from "domain/entities/expense";
import expenseRepository from "infra/repositories/expense-repository";
import { Logger } from "../../infra/logger/logger";
import { ExpenseInputFilter } from "domain/models/expense-input-filter";

export const useCreateExpenseMutation = () => {
    const logger = Logger.getInstance();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: any) => {
            const result = await expenseRepository.create(data);
            return result;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["expenses", {}] });
        },
        onError: (error: any) => {
            if (error instanceof AxiosError) {
                logger.error(
                    error.response?.data.metadata.errors[0],
                    error.response?.data.metadata
                );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        },
    });
};

export const useUpdateExpenseMutation = () => {
    const logger = Logger.getInstance();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: Expense) => {
            const result = await expenseRepository.update(data);
            return result;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["expenses", {}] });
        },
        onError: (error: any) => {
            if (error instanceof AxiosError) {
                logger.error(
                    error.response?.data.metadata.errors[0],
                    error.response?.data.metadata
                );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        },
    });
};

export const useDeleteExpenseMutation = () => {
    const logger = Logger.getInstance();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const result = await expenseRepository.delete(id);
            return result;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["expenses", {}] });
        },
        onError: (error: any) => {
            if (error instanceof AxiosError) {
                logger.error(
                    error.response?.data.metadata.errors[0],
                    error.response?.data.metadata
                );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        },
    });
};

export const useExpenseById = (id?: string) => {
    const logger = Logger.getInstance();

    return useQuery({
        queryKey: ["expense", id],
        queryFn: async () => {
            try {
                if (!id) return;
                const result = await expenseRepository.getById(id);

                return result;
            } catch (error: any) {
                if (error instanceof AxiosError) {
                    logger.error(
                        error.response?.data.metadata.errors[0],
                        error.response?.data.metadata
                    );
                    throw new Error(error.response?.data.metadata.errors);
                }
                throw error;
            }
        },
        enabled: !!id,
        retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
        staleTime: 0,
    });
};

export const useExpenseByFilter = (filter: ExpenseInputFilter) => {
    const logger = Logger.getInstance();

    return useQuery({
        queryKey: ["expenses", filter],
        queryFn: async () => {
            try {
                const result = await expenseRepository.getByFilter(filter);

                return result;
            } catch (error: any) {
                if (error instanceof AxiosError) {
                    logger.error(
                        error.response?.data.metadata.errors[0],
                        error.response?.data.metadata
                    );
                    throw new Error(error.response?.data.metadata.errors);
                }
                throw error;
            }
        },
        retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
        staleTime: 0,
    });
};
